import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Grid, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, CreditCardOutlined, EyeOutlined, FilePdfOutlined, FileTextOutlined, ReloadOutlined, StopOutlined, WalletOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { companiesService, receiversService, userService } from '../../services/api';
import AddPaymentModal from './components/AddPaymentModal';
import { computeFifoDistribution } from './utils/cobranzaPpd';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const csvEscape = (value) => {
  const raw = value == null ? '' : String(value);
  const escaped = raw.replace(/"/g, '""');
  return `"${escaped}"`;
};

const buildCsv = (headers, rows) => {
  const headerLine = headers.map(csvEscape).join(',');
  const dataLines = rows.map((r) => r.map(csvEscape).join(','));
  return `\uFEFF${[headerLine, ...dataLines].join('\r\n')}`;
};

const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const CobranzaPPD = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const topPaginationRef = useRef(null);
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);

  const [companyFilter, setCompanyFilter] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [uuidFilter, setUuidFilter] = useState('');
  const [dateRange, setDateRange] = useState([]);

  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [exportingCsv, setExportingCsv] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const selectedInvoices = useMemo(() => invoices.filter((i) => selectedRowKeys.includes(i.id)), [invoices, selectedRowKeys]);
  const selectedSaldoTotal = useMemo(() => {
    const sum = selectedInvoices.reduce((acc, i) => acc + Number(i?.saldo_pendiente || 0), 0);
    return Number(sum.toFixed(2));
  }, [selectedInvoices]);

  const [singleInvoicePaymentOpen, setSingleInvoicePaymentOpen] = useState(false);
  const [singleInvoicePaymentInvoice, setSingleInvoicePaymentInvoice] = useState(null);
  const [singleInvoicePaymentSource, setSingleInvoicePaymentSource] = useState(null);

  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
  const [paymentsInvoice, setPaymentsInvoice] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payments, setPayments] = useState([]);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelPayment, setCancelPayment] = useState(null);
  const [cancelMotives, setCancelMotives] = useState([]);
  const [cancelForm] = Form.useForm();
  const [cancelLoading, setCancelLoading] = useState(false);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkForm] = Form.useForm();
  const [distribution, setDistribution] = useState({});
  const [bulkStamping, setBulkStamping] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const loadCompanies = async () => {
    const resp = await companiesService.getCompanies();
    setCompanies(resp?.data?.companies || []);
  };

  const loadClients = async (search = '') => {
    const resp = await receiversService.getReceivers(search);
    setClients(resp?.data || []);
  };

  const fetchInvoices = async (page = 1, pageSize = pagination.pageSize, overrides = {}) => {
    const limit = pageSize;
    const offset = (page - 1) * pageSize;
    const filters = {
      company_id: overrides.company_id ?? companyFilter,
      receiver_id: overrides.receiver_id ?? clientFilter,
      uuid: overrides.uuid ?? uuidFilter,
      date_from: overrides.date_from ?? (dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : null),
      date_to: overrides.date_to ?? (dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : null),
    };

    setInvoicesLoading(true);
    try {
      const resp = await userService.getCobranzaPpdInvoices(limit, offset, filters);
      const data = resp?.data ?? resp;
      const list = data?.invoices ?? [];
      const pag = data?.pagination ?? {};

      setInvoices(list);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: pag.total ?? list.length,
      }));
      setSelectedRowKeys([]);
    } catch (e) {
      message.error('No se pudieron cargar las facturas PPD.');
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadCompanies(), loadClients('')]);
      await fetchInvoices(1, 10, {});
    };
    init();
  }, []);

  const handleClientSearch = async (value) => {
    await loadClients(value);
  };

  const exportCobranzaCsv = async () => {
    setExportingCsv(true);
    try {
      const filters = {
        company_id: companyFilter,
        receiver_id: clientFilter,
        uuid: uuidFilter,
        date_from: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : null,
        date_to: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : null,
      };

      const pageSize = 100;
      const all = [];
      let offset = 0;
      let total = null;

      while (true) {
        const resp = await userService.getCobranzaPpdInvoices(pageSize, offset, filters);
        const data = resp?.data ?? resp;
        const list = data?.invoices ?? [];
        const pag = data?.pagination ?? {};
        if (total == null) total = pag.total ?? null;
        all.push(...list);
        if (list.length === 0) break;
        if (total != null && all.length >= total) break;
        offset += pageSize;
        if (offset > 100000) break;
      }

      if (all.length === 0) {
        message.info('No hay facturas PPD para exportar con los filtros actuales.');
        return;
      }

      const headers = [
        'Serie',
        'Folio',
        'UUID',
        'RFC Cliente',
        'Cliente',
        'Empresa',
        'Moneda',
        'Total',
        'Pagado',
        'Saldo pendiente',
        'Estatus',
        'Fecha creación',
      ];

      const rows = all.map((row) => [
        row?.serie || '',
        row?.folio || '',
        row?.uuid || row?.folio_fiscal || '',
        row?.receiver?.tax_id || '',
        row?.receiver?.tax_name || '',
        row?.company?.tax_name || row?.company?.business_name || '',
        row?.moneda || '',
        Number(row?.total || 0).toFixed(2),
        Number(row?.monto_pagado || 0).toFixed(2),
        Number(row?.saldo_pendiente || 0).toFixed(2),
        row?.status || '',
        row?.created_at ? new Date(row.created_at).toISOString() : '',
      ]);

      const csv = buildCsv(headers, rows);
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      downloadCsv(csv, `cobranza_ppd_${stamp}.csv`);
      message.success(`CSV exportado (${all.length} facturas).`);
    } catch (e) {
      message.error('No se pudo exportar el CSV.');
    } finally {
      setExportingCsv(false);
    }
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',') ? base64Content.split(',').pop() : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'Pago.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadResponseFile = (response, defaultFilename) => {
    const disposition = response?.headers?.['content-disposition'] || response?.headers?.get?.('content-disposition');
    let filename = defaultFilename;
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename="(.+?)"/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openApplyPayment = (invoice) => {
    setSingleInvoicePaymentInvoice(invoice);
    setSingleInvoicePaymentOpen(true);
  };

  const fetchPaymentsForInvoice = async (invoice) => {
    if (!invoice?.id) return;
    setPaymentsLoading(true);
    try {
      const resp = await userService.getPayments(200, 0, { invoice_id: invoice.id });
      const list = resp?.data?.payments ?? resp?.payments ?? [];
      setPayments(Array.isArray(list) ? list : []);
    } catch (e) {
      setPayments([]);
      message.error('No se pudieron cargar los pagos.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const openPaymentsModal = async (invoice) => {
    setPaymentsInvoice(invoice);
    setPaymentsModalOpen(true);
    await fetchPaymentsForInvoice(invoice);
  };

  const getPaymentSubstitutionOptions = (payment) => {
    if (!payment) return [];

    return (payments || [])
      .filter((item) => item?.id !== payment.id && item?.status === 'timbrada' && !!item?.uuid)
      .map((item) => ({
        value: item.uuid,
        label: `${item?.serie || '—'}-${item?.folio ?? '—'} | ${item.uuid}`,
      }));
  };

  const ensureCancellationMotives = async () => {
    if (cancelMotives.length > 0) return;
    const resp = await userService.getCancellationMotives();
    const list = resp?.data ?? resp ?? [];
    setCancelMotives(list);
  };

  const openCancelPayment = async (payment, defaults = {}) => {
    await ensureCancellationMotives();
    const cancellationCheck = await userService.checkPaymentCancellation(payment.id);
    const canCancel = cancellationCheck?.data?.can_cancel ?? cancellationCheck?.can_cancel;
    const reason = cancellationCheck?.data?.reason ?? cancellationCheck?.reason;

    if (!canCancel) {
      message.warning(reason || 'El pago no se puede cancelar.');
      return;
    }

    setCancelPayment(payment);
    cancelForm.resetFields();
    cancelForm.setFieldsValue({
      motivo: defaults.motivo || '02',
      uuid_sustitucion: defaults.uuid_sustitucion || '',
    });
    setCancelModalOpen(true);
  };

  const openReplacePayment = (payment) => {
    setSingleInvoicePaymentInvoice(paymentsInvoice);
    setSingleInvoicePaymentSource(payment);
    setSingleInvoicePaymentOpen(true);
  };

  const openCancelPaymentWithReplacement = async (payment) => {
    const options = getPaymentSubstitutionOptions(payment);

    if (options.length === 0) {
      message.info('Primero crea y timbra el pago sustituto para poder cancelar el incorrecto con motivo 01.');
    }

    await openCancelPayment(payment, {
      motivo: '01',
      uuid_sustitucion: options[0]?.value || '',
    });
  };

  const doCancelPayment = async () => {
    try {
      const values = await cancelForm.validateFields();
      setCancelLoading(true);
      await userService.cancelPayment(cancelPayment.id, {
        motivo: values.motivo,
        uuid_sustitucion: values.uuid_sustitucion || null,
      });
      message.success('Pago cancelado');
      setCancelModalOpen(false);
      await fetchPaymentsForInvoice(paymentsInvoice);
      await fetchInvoices(pagination.current, pagination.pageSize, {});
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Error al cancelar el pago.';
      message.error(errMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const stampDraftPayment = async (payment) => {
    try {
      await Modal.confirm({
        title: 'Timbrar pago',
        content: 'Se timbrará el complemento de pago (REP).',
        okText: 'Timbrar',
        cancelText: 'Cancelar',
        onOk: async () => {
          await userService.stampPayment(payment.id);
        },
      });
      message.success('Pago timbrado');
      await fetchPaymentsForInvoice(paymentsInvoice);
      await fetchInvoices(pagination.current, pagination.pageSize, {});
    } catch (e) {
      const errMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Error al timbrar el pago.';
      if (e?.message !== 'cancelled') {
        message.error(errMsg);
      }
    }
  };

  const downloadPaymentPdf = async (payment) => {
    try {
      const resp = await userService.generatePaymentsPdf(payment.id, { templateId: 'payment20' });
      const data = resp?.data ?? resp;
      downloadBase64Pdf(data?.pdf_content, data?.filename);
    } catch (e) {
      message.error('No se pudo descargar el PDF del pago.');
    }
  };

  const downloadPaymentXml = async (payment) => {
    try {
      const resp = await userService.downloadPaymentsXml(payment.id);
      downloadResponseFile(resp, `Pago_${payment.id}.xml`);
    } catch (e) {
      message.error('No se pudo descargar el XML del pago.');
    }
  };

  const openBulkDeposit = () => {
    if (selectedInvoices.length === 0) {
      message.warning('Selecciona al menos una factura.');
      return;
    }
    const companyIds = Array.from(new Set(selectedInvoices.map((i) => i.company_id)));
    const receiverIds = Array.from(new Set(selectedInvoices.map((i) => i.receiver_id)));
    if (companyIds.length !== 1 || receiverIds.length !== 1) {
      message.error('Las facturas seleccionadas deben pertenecer a la misma empresa y al mismo receptor.');
      return;
    }

    bulkForm.setFieldsValue({
      fecha_pago: moment(),
      forma_pago: '03',
      moneda: 'MXN',
      tipo_cambio: 1,
      monto_total: selectedSaldoTotal,
    });
    const { distribution: dist } = computeFifoDistribution(selectedInvoices, selectedSaldoTotal);
    setDistribution(dist);
    setBulkResult(null);
    setBulkModalOpen(true);
  };

  const totalDistributed = useMemo(() => {
    const sum = Object.values(distribution).reduce((acc, v) => acc + Number(v || 0), 0);
    return Number(sum.toFixed(2));
  }, [distribution]);

  const stampBulkDeposit = async () => {
    try {
      const values = await bulkForm.validateFields();
      const montoTotal = Number(values.monto_total || 0);
      if (montoTotal <= 0) {
        message.error('El monto total debe ser mayor a 0.');
        return;
      }
      if (montoTotal - selectedSaldoTotal > 0.00001) {
        message.error('El monto total excede el saldo pendiente total seleccionado.');
        return;
      }
      if (Math.abs(totalDistributed - montoTotal) > 0.01) {
        message.error('La suma distribuida debe ser igual al monto total.');
        return;
      }

      const invoiceIds = selectedInvoices.filter((inv) => Number(distribution[inv.id] || 0) > 0).map((inv) => inv.id);
      if (invoiceIds.length === 0) {
        message.error('La distribución no aplica monto a ninguna factura.');
        return;
      }

      await new Promise((resolve, reject) => {
        Modal.confirm({
          title: 'Confirmar timbrado',
          content: `Se timbrarán ${invoiceIds.length} complementos de pago (uno por factura).`,
          okText: 'Timbrar',
          cancelText: 'Cancelar',
          onOk: resolve,
          onCancel: () => reject(new Error('cancelled')),
        });
      });

      setBulkStamping(true);
      const resp = await userService.stampBulkPpdPayments({
        invoice_ids: invoiceIds,
        fecha_pago: values.fecha_pago.format('YYYY-MM-DD'),
        forma_pago: values.forma_pago,
        moneda: values.moneda,
        tipo_cambio: values.tipo_cambio,
        monto_total: montoTotal,
        distribution,
      });
      const data = resp?.data ?? resp;
      setBulkResult(data);

      const ok = (data?.results ?? []).filter((r) => r.status === 'success').length;
      const err = (data?.results ?? []).filter((r) => r.status === 'error').length;
      if (err > 0) {
        message.warning(`Proceso finalizado: ${ok} timbrados, ${err} con error.`);
      } else {
        message.success(`Proceso finalizado: ${ok} timbrados.`);
      }

      await fetchInvoices(pagination.current, pagination.pageSize, {});
    } catch (e) {
      if (e?.message === 'cancelled') return;
      const errMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Error al timbrar pagos.';
      message.error(errMsg);
    } finally {
      setBulkStamping(false);
    }
  };

  const invoiceColumns = [
    {
      title: 'Serie/Folio',
      key: 'serie_folio',
      ellipsis: true,
      width: screens.xs ? 80 : 90,
      render: (_, row) => {
        const serie = row?.serie || '';
        const folio = row?.folio || '';
        const compact = [serie, folio].filter(Boolean).join('-') || '—';
        return compact;
      },
    },
    {
      title: 'Cliente',
      key: 'client_name',
      ellipsis: true,
      width: screens.xs ? 120 : 220,
      render: (_, row) => {
        const rfc = row?.receiver?.tax_id || 'N/A';
        const name = row?.receiver?.tax_name || 'N/A';
        const label = screens.xs ? rfc : `${rfc} - ${name}`;
        return (
          <Tooltip title={label}>
            <span>{label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total / Saldo',
      key: 'total_balance',
      ellipsis: true,
      width: 100,
      render: (_, row) => {
        const totalNum = Number(row?.total || 0);
        const saldo = Number(row?.saldo_pendiente || 0);
        const tooltip = `$${totalNum.toFixed(2)} MXN | ${saldo <= 0.01 ? 'Pagada' : `Debe: $${saldo.toFixed(2)}`}`;
        return (
          <Tooltip title={tooltip}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ whiteSpace: 'nowrap' }}>${totalNum.toFixed(2)}</div>
              <span style={{ fontSize: 12, color: saldo <= 0.01 ? '#389e0d' : '#cf1322', whiteSpace: 'nowrap' }}>
                {saldo <= 0.01 ? 'Pagada' : `Debe: $${saldo.toFixed(2)}`}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: screens.xs ? 120 : 160,
      render: (_, record) => (
        <Space size={2} wrap>
          <Tooltip title="Ver">
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/invoices/${record.id}`, { state: { from: 'cobranza' } })}
            />
          </Tooltip>
          <Tooltip title="Aplicar pago">
            <Button
              shape="circle"
              size="small"
              icon={<WalletOutlined />}
              onClick={() => openApplyPayment(record)}
            />
          </Tooltip>
          <Tooltip title="Pagos">
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={<CreditCardOutlined />}
              onClick={() => openPaymentsModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const paymentsColumns = [
    {
      title: 'Fecha de pago',
      key: 'fecha_pago',
      render: (_, row) => (row?.fecha_pago ? new Date(row.fecha_pago).toLocaleDateString() : '—'),
    },
    {
      title: 'Serie',
      dataIndex: 'serie',
      key: 'serie',
      render: (v) => v || '—',
    },
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      render: (v) => (v == null || v === '' ? '—' : String(v)),
    },
    {
      title: 'Monto',
      key: 'monto',
      render: (_, row) => `$${Number(row?.monto || 0).toFixed(2)} ${row?.moneda || 'MXN'}`,
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (v) => {
        if (v === 'timbrada') return <Tag color="green">Timbrado</Tag>;
        if (v === 'cancelada') return <Tag color="red">Cancelado</Tag>;
        return <Tag color="gold">Borrador</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, row) => (
        <Space size={2} wrap>
          {row?.status === 'borrador' && (
            <Tooltip title="Timbrar">
              <Button shape="circle" size="small" icon={<ReloadOutlined />} onClick={() => stampDraftPayment(row)} />
            </Tooltip>
          )}
          {row?.status === 'timbrada' && (
            <>
              <Tooltip title="Sustituir">
                <Button shape="circle" size="small" icon={<CopyOutlined />} onClick={() => openReplacePayment(row)} />
              </Tooltip>
              <Tooltip title="PDF">
                <Button shape="circle" size="small" icon={<FilePdfOutlined />} onClick={() => downloadPaymentPdf(row)} />
              </Tooltip>
              <Tooltip title="XML">
                <Button shape="circle" size="small" icon={<FileTextOutlined />} onClick={() => downloadPaymentXml(row)} />
              </Tooltip>
              <Tooltip title="Cancelar con sustitución">
                <Button shape="circle" size="small" icon={<ReloadOutlined />} onClick={() => openCancelPaymentWithReplacement(row)} />
              </Tooltip>
              <Tooltip title="Cancelar">
                <Button shape="circle" size="small" danger icon={<StopOutlined />} onClick={() => openCancelPayment(row)} />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '12px' : '24px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'stretch' : 'center',
          gap: '12px',
          marginBottom: screens.xs ? '16px' : '24px',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '20px' : '24px' }}>
          Cobranza
        </Title>
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: screens.xs ? '100%' : 'auto' }}>
          <Button icon={<ReloadOutlined />} onClick={() => fetchInvoices(1, pagination.pageSize, {})} block={screens.xs}>
            Recargar
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportCobranzaCsv} loading={exportingCsv} block={screens.xs}>
            Exportar CSV
          </Button>
          <Button type="primary" icon={<CreditCardOutlined />} onClick={openBulkDeposit} disabled={selectedInvoices.length === 0} block={screens.xs}>
            Depósito múltiple
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Card size="small" bodyStyle={{ padding: '12px' }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Empresa"
                allowClear
                value={companyFilter}
                onChange={(v) => {
                  setCompanyFilter(v);
                  fetchInvoices(1, pagination.pageSize, { company_id: v });
                }}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {companies.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.tax_name}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Cliente"
                allowClear
                value={clientFilter}
                onChange={(v) => {
                  setClientFilter(v);
                  fetchInvoices(1, pagination.pageSize, { receiver_id: v });
                }}
                style={{ width: '100%' }}
                showSearch
                filterOption={false}
                onSearch={handleClientSearch}
                notFoundContent={null}
                defaultActiveFirstOption={false}
                showArrow={false}
              >
                {clients.map((client) => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.tax_name || client.tax_id}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6} lg={5}>
              <Input
                placeholder="Buscar por UUID"
                value={uuidFilter}
                onChange={(e) => setUuidFilter(e.target.value)}
                allowClear
                style={{ width: '100%' }}
                onPressEnter={() => fetchInvoices(1, pagination.pageSize, { uuid: uuidFilter })}
              />
            </Col>

            <Col xs={24} sm={12} md={6} lg={5}>
              <DatePicker.RangePicker
                onChange={(range) => {
                  setDateRange(range || []);
                  const dateFrom = range?.[0] ? range[0].format('YYYY-MM-DD') : null;
                  const dateTo = range?.[1] ? range[1].format('YYYY-MM-DD') : null;
                  fetchInvoices(1, pagination.pageSize, { date_from: dateFrom, date_to: dateTo });
                }}
                style={{ width: '100%' }}
                placeholder={['Inicio', 'Fin']}
              />
            </Col>

            <Col xs={24} sm={12} md={4} lg={2}>
              <Button
                onClick={() => {
                  setUuidFilter('');
                  setDateRange([]);
                  setCompanyFilter(null);
                  setClientFilter(null);
                  fetchInvoices(1, pagination.pageSize, { uuid: '', date_from: null, date_to: null, company_id: null, receiver_id: null });
                }}
                block
              >
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Space>
          <Text>Seleccionadas:</Text>
          <Tag>{selectedInvoices.length}</Tag>
          <Text>Saldo total:</Text>
          <Tag color={selectedSaldoTotal > 0 ? 'orange' : 'green'}>${selectedSaldoTotal.toFixed(2)}</Tag>
        </Space>
      </div>

      <div ref={topPaginationRef} />
      <Table
        columns={invoiceColumns.filter((c) => !screens.xs || ['serie_folio', 'client_name', 'total_balance', 'actions'].includes(c.key))}
        dataSource={invoices}
        loading={invoicesLoading}
        rowKey="id"
        tableLayout="fixed"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          getCheckboxProps: (record) => ({
            disabled: Number(record?.saldo_pendiente || 0) <= 0,
          }),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} facturas`,
        }}
        onChange={(p) => fetchInvoices(p.current, p.pageSize, {})}
      />

      <AddPaymentModal
        invoice={singleInvoicePaymentInvoice}
        open={singleInvoicePaymentOpen}
        prefillPayment={singleInvoicePaymentSource}
        onOpenChange={(v) => {
          setSingleInvoicePaymentOpen(v);
          if (!v) {
            setSingleInvoicePaymentInvoice(null);
            setSingleInvoicePaymentSource(null);
            fetchInvoices(pagination.current, pagination.pageSize, {});
            if (paymentsInvoice) {
              fetchPaymentsForInvoice(paymentsInvoice);
            }
          }
        }}
        hideTrigger
        title={singleInvoicePaymentSource ? 'Sustituir pago' : 'Aplicar pago'}
        onCreated={() => {
          fetchInvoices(pagination.current, pagination.pageSize, {});
          if (paymentsInvoice) {
            fetchPaymentsForInvoice(paymentsInvoice);
          }
        }}
      />

      <Modal
        title="Pagos"
        open={paymentsModalOpen}
        onCancel={() => {
          setPaymentsModalOpen(false);
          setPaymentsInvoice(null);
          setPayments([]);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {paymentsInvoice?.uuid ? `Factura UUID: ${paymentsInvoice.uuid}` : ''}
          </Text>
        </div>
        <Table
          rowKey="id"
          size="small"
          dataSource={payments}
          columns={paymentsColumns}
          loading={paymentsLoading}
          pagination={false}
        />
      </Modal>

      <Modal
        title="Cancelar pago"
        open={cancelModalOpen}
        onCancel={() => {
          setCancelModalOpen(false);
          setCancelPayment(null);
        }}
        onOk={doCancelPayment}
        okText="Cancelar CFDI"
        okButtonProps={{ danger: true, loading: cancelLoading }}
        cancelText="Cerrar"
        destroyOnClose
      >
        {cancelPayment && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              Pago a cancelar: {cancelPayment?.serie || '—'}-{cancelPayment?.folio ?? '—'} {cancelPayment?.uuid ? `| UUID: ${cancelPayment.uuid}` : ''}
            </Text>
          </div>
        )}
        <Form
          form={cancelForm}
          layout="vertical"
          onValuesChange={(changed) => {
            if (changed.motivo && changed.motivo !== '01') {
              cancelForm.setFieldsValue({ uuid_sustitucion: '' });
            }
          }}
        >
          <Form.Item
            label="Motivo"
            name="motivo"
            rules={[{ required: true, message: 'Selecciona el motivo' }]}
          >
            <Select
              options={cancelMotives.map((m) => ({ value: m.code, label: `${m.code} - ${m.description}` }))}
            />
          </Form.Item>
          <Form.Item
            label="UUID sustitución (solo motivo 01)"
            name="uuid_sustitucion"
            dependencies={['motivo']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const motivo = getFieldValue('motivo');
                  if (motivo !== '01') return Promise.resolve();
                  if (value && String(value).trim().length > 0) return Promise.resolve();
                  return Promise.reject(new Error('Para el motivo 01 se requiere UUID sustitución'));
                },
              }),
            ]}
          >
            {getPaymentSubstitutionOptions(cancelPayment).length > 0 ? (
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecciona el pago sustituto"
                options={getPaymentSubstitutionOptions(cancelPayment)}
              />
            ) : (
              <Input placeholder="UUID sustitución" />
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Depósito múltiple"
        open={bulkModalOpen}
        onCancel={() => setBulkModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form
          form={bulkForm}
          layout="vertical"
          onValuesChange={(changed, all) => {
            if ('moneda' in changed && changed.moneda === 'MXN') {
              bulkForm.setFieldsValue({ tipo_cambio: 1 });
            }
            if ('monto_total' in changed) {
              const { distribution: dist } = computeFifoDistribution(selectedInvoices, Number(all.monto_total || 0));
              setDistribution(dist);
            }
          }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} md={6}>
              <Form.Item label="Fecha de pago" name="fecha_pago" rules={[{ required: true, message: 'Selecciona la fecha de pago' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Forma de pago" name="forma_pago" rules={[{ required: true, message: 'Selecciona la forma de pago' }]}>
                <Select
                  options={[
                    { value: '01', label: '01 - Efectivo' },
                    { value: '02', label: '02 - Cheque nominativo' },
                    { value: '03', label: '03 - Transferencia electrónica' },
                    { value: '04', label: '04 - Tarjeta de crédito' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Moneda" name="moneda" rules={[{ required: true, message: 'Selecciona la moneda' }]}>
                <Select options={[{ value: 'MXN', label: 'MXN' }, { value: 'USD', label: 'USD' }]} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Tipo de cambio" name="tipo_cambio" rules={[{ required: true, message: 'Indica el tipo de cambio' }]}>
                <InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Form.Item label="Monto total a aplicar" name="monto_total" rules={[{ required: true, message: 'Indica el monto total' }]}>
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Card size="small" bodyStyle={{ padding: 12 }}>
                <Text type="secondary">Saldo seleccionado:</Text> <Text strong>${selectedSaldoTotal.toFixed(2)}</Text>
                <span style={{ marginLeft: 12 }} />
                <Text type="secondary">Distribuido:</Text> <Text strong>${totalDistributed.toFixed(2)}</Text>
              </Card>
            </Col>
          </Row>

          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={selectedInvoices}
            columns={[
              { title: 'Serie/Folio', key: 'sf', render: (_, r) => [r?.serie, r?.folio].filter(Boolean).join('-') || '—' },
              { title: 'Saldo', key: 'saldo', render: (_, r) => `$${Number(r?.saldo_pendiente || 0).toFixed(2)}` },
              {
                title: 'Aplicar',
                key: 'apply',
                render: (_, r) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={distribution[r.id] ?? 0}
                    max={Number(r?.saldo_pendiente || 0)}
                    onChange={(v) => setDistribution((prev) => ({ ...prev, [r.id]: Number((v || 0).toFixed(2)) }))}
                  />
                ),
              },
            ]}
          />

          {bulkResult?.results && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Resultado</Text>
              <Table
                rowKey={(r) => `${r.invoice_id}-${r.status}`}
                size="small"
                pagination={false}
                dataSource={bulkResult.results}
                columns={[
                  { title: 'Factura UUID', dataIndex: 'invoice_uuid', key: 'invoice_uuid', ellipsis: true },
                  { title: 'Aplicado', dataIndex: 'importe_aplicado', key: 'importe_aplicado', render: (v) => `$${Number(v || 0).toFixed(2)}` },
                  { title: 'REP UUID', dataIndex: 'payment_uuid', key: 'payment_uuid', ellipsis: true, render: (v) => v || '—' },
                  { title: 'Estado', dataIndex: 'status', key: 'status', render: (v) => (v === 'success' ? <Tag color="green">OK</Tag> : <Tag color="red">Error</Tag>) },
                  { title: 'Error', dataIndex: 'error', key: 'error', ellipsis: true, render: (v) => v || '—' },
                ]}
              />
            </div>
          )}

          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button onClick={() => setBulkModalOpen(false)}>Cerrar</Button>
              <Button type="primary" loading={bulkStamping} onClick={stampBulkDeposit}>
                Timbrar complementos
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CobranzaPPD;
